// src/pages/Attest.js
//
// RULING 330 Part 3 - the NO-LOGIN officer attestation page. Reached at /attest?t=<token>&a=<applicationId> from the
// contentless e-mail notice (src\data\attestationLink.js attestNotice). The officer - the routed Local Chairperson or the
// Local's Secretary-Treasurer - has no account; the single-use token in the link is the credential. The page shows the
// applicant and three acts: Confirm / Not on my roster / Not in good standing. It INFORMS; the Local Chairperson decides
// (ruling 193). The applicant never sees who answered (ruling 330.3). A used, expired, or wrong-address link is refused.
import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { startAppCheck } from '../appCheck';
import { PLATFORM_NAME } from '../installation';

const C = { bg: '#0A1526', panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #16253c' }}>
      <span style={{ color: C.dim, fontSize: 12 }}>{k}</span>
      <span style={{ color: C.text, fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{v || '—'}</span>
    </div>
  );
}

export default function Attest() {
  const params = new URLSearchParams(window.location.search);
  const token = (params.get('t') || '').trim();
  const applicationId = (params.get('a') || '').trim();
  const [state, setState] = useState({ loading: true });
  const [email, setEmail] = useState('');
  const [rosterNote, setRosterNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { startAppCheck(); } catch (e) { /* App Check is best-effort here */ }
      try {
        const r = await httpsCallable(getFunctions(), 'viewAttestation')({ token, applicationId });
        setState({ loading: false, ...r.data });
      } catch (e) {
        setState({ loading: false, ok: false, words: 'This link could not be opened. Ask your General Chairperson for a new one.' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (kind) => {
    setBusy(true);
    try {
      const r = await httpsCallable(getFunctions(), 'redeemAttestation')({ token, applicationId, email: email.trim().toLowerCase(), act: kind, rosterAttached: rosterNote });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, words: 'This could not be recorded. Ask your General Chairperson for a new link.' });
    }
    setBusy(false);
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, display: 'flex', justifyContent: 'center', padding: '32px 16px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 520, width: '100%' }}>
        <div style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: C.dim, marginBottom: 18 }}>{PLATFORM_NAME || 'Labor On The Record'}</div>
        {children}
      </div>
    </div>
  );

  if (state.loading) return shell(<div style={{ color: C.dim }}>Opening…</div>);
  if (!state.ok) return shell(
    <div style={{ border: '1px solid ' + C.bad, borderRadius: 6, padding: 18 }}>
      <div style={{ color: C.bad, fontWeight: 700 }}>This link cannot be used</div>
      <div style={{ color: C.dim, marginTop: 8 }}>{state.words}</div>
    </div>
  );
  if (result) return shell(
    result.ok
      ? <div style={{ border: '1px solid ' + C.ok, borderRadius: 6, padding: 18 }}>
          <div style={{ color: C.ok, fontWeight: 700 }}>Recorded. Thank you.</div>
          <div style={{ color: C.dim, marginTop: 8 }}>You {result.finding}. Your Local Chairperson decides the application; this confirmation informs that decision.</div>
        </div>
      : <div style={{ border: '1px solid ' + C.bad, borderRadius: 6, padding: 18 }}>
          <div style={{ color: C.bad, fontWeight: 700 }}>Not recorded</div>
          <div style={{ color: C.dim, marginTop: 8 }}>{result.words}</div>
        </div>
  );

  const a = state.applicant || {};
  const acts = state.acts || {};
  return shell(
    <div>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Confirm an application</h1>
      <div style={{ color: C.dim, fontSize: 13, marginBottom: 16 }}>You were asked to confirm this as {state.officeLabel}. Your answer informs the Local Chairperson's decision; it does not decide it.</div>
      {state.decided ? <div style={{ color: C.accent, fontSize: 12, marginBottom: 12 }}>Note: this application has already been decided. Your answer is still recorded.</div> : null}
      <div style={{ border: '1px solid ' + C.line, background: C.panel, borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <Row k="Name" v={a.name} />
        <Row k="Roster number" v={a.rosterNumber} />
        <Row k="Seniority date" v={a.seniorityDate} />
        <Row k="Local" v={a.local} />
        <Row k="Committee" v={a.committee} />
      </div>
      <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>Confirm your e-mail of record (the address this link was sent to):</div>
      <input aria-label="Your e-mail of record" className="form-input" style={{ width: '100%', marginBottom: 12 }} type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: C.dim, marginBottom: 16 }}>
        <input type="checkbox" checked={rosterNote} onChange={(e) => setRosterNote(e.target.checked)} style={{ marginTop: 3 }} />
        <span>I will send my roster to the General Chairperson so it can be put on file.</span>
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button className="btn btn-primary" disabled={busy || !email.trim()} onClick={() => act('confirm')}>{acts.confirm ? acts.confirm.words : 'Confirm'}</button>
        <button className="btn btn-secondary" disabled={busy || !email.trim()} onClick={() => act('not_on_roster')}>{acts.not_on_roster ? acts.not_on_roster.words : 'Not on my roster'}</button>
        <button className="btn btn-secondary" disabled={busy || !email.trim()} onClick={() => act('not_in_good_standing')}>{acts.not_in_good_standing ? acts.not_in_good_standing.words : 'Not in good standing'}</button>
      </div>
      <div style={{ color: C.dim, fontSize: 11, marginTop: 14 }}>This link works once and expires 7 days after it was sent. The applicant never sees who answered.</div>
    </div>
  );
}
